import React, { useMemo } from "react";
import { View, StyleSheet } from "react-native";
import NtText from "../../components/NtText";
import NtTouchableEffect from "../../components/NtTouchableEffect";
import NtMaterialIcon from "../../components/NtMaterialIcon";
import { useTheme } from "../../context/ThemeContext";
import NtVoxcraftBrand from "../../marketing/components/voxcraft/NtVoxcraftBrand";

/**
 * Top brand bar — used by both onboarding and setup wizards.
 *
 * Props:
 *   steps:        Array of step objects (each with `title`)
 *   currentStep:  Index into `steps`
 *   onClose:      Optional close handler. When provided, renders a "Close
 *                 wizard" button on the right. Used by the post-signin setup
 *                 wizard so users can bail back to the dashboard.
 *   closeLabel:   Custom label for the close button (default "Exit wizard").
 */
function NtOnboardingTopRail({ steps, currentStep, onClose, closeLabel = "Exit wizard" }) {
  const { theme } = useTheme();
  const styles = useMemo(() => makeStyles(theme), [theme]);
  const step = steps?.[currentStep];
  const total = steps?.length || 0;
  return (
    <View style={styles.rail}>
      <View style={styles.brand}>
        <NtVoxcraftBrand size={26} fontSize={17} />
      </View>
      <View style={styles.right}>
        <NtText style={styles.progress}>
          Step {currentStep + 1} of {total} · {step?.title}
        </NtText>
        {onClose ? (
          <>
            <View style={styles.divider} />
            <NtTouchableEffect style={styles.closeBtn} onPress={onClose}>
              <NtMaterialIcon name="close" size={14} color={theme.textDim} />
              <NtText style={styles.closeText}>{closeLabel}</NtText>
            </NtTouchableEffect>
          </>
        ) : null}
      </View>
    </View>
  );
}

const makeStyles = (t) =>
  StyleSheet.create({
    rail: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      paddingHorizontal: 36,
      paddingVertical: 16,
      backgroundColor: t.surface,
      borderBottomWidth: 1,
      borderBottomColor: t.borderSoft,
    },
    brand: { flexDirection: "row", alignItems: "center" },
    right: { flexDirection: "row", alignItems: "center", gap: 16 },
    progress: {
      fontSize: 11,
      fontWeight: "600",
      color: t.textDim,
      letterSpacing: 0.2,
    },
    divider: { width: 1, height: 18, backgroundColor: t.borderSoft },
    closeBtn: {
      flexDirection: "row",
      alignItems: "center",
      gap: 6,
      paddingHorizontal: 12,
      paddingVertical: 7,
      borderRadius: t.radius,
      borderWidth: 1,
      borderColor: t.border,
      backgroundColor: t.surface2,
    },
    closeText: {
      fontSize: 12,
      color: t.text,
      fontWeight: "600",
    },
  });

export default NtOnboardingTopRail;
